import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import type {
  LibraTicket,
  LibraTicketGroup,
  SiteRoiAssumptionsValue,
} from 'features/libra/ticket/libraTicketsApi'
import {
  TAR_ROI_GLOBAL_STORAGE_KEY,
  TAR_ROI_GROUP_DATA_KEY,
} from 'features/libra/ticket/libraTicketsApi'
import {
  ELIGIBILITY_STATUS,
  type EligibilityStatus,
} from 'features/libra/types/eligibility'
import groupBy from 'lodash/groupBy'
import uniq from 'lodash/uniq'

/** Ontology field id (libra-ticket) — inspectable area feeds the inspection cost delta. */
export const INSPECTABLE_AREA_M2_FIELD = 'inspectable_area_m2'

const UNKNOWN_SITE_KEY = '__unknown_site__'

export type RoiSavingsCategory = 'inspection' | 'hse' | 'production' | 'labor'

export type RoiSavingsBreakdown = Record<RoiSavingsCategory, number> & {
  total: number
}

/** Merged global + TAR-level parameters after applying defaults and legacy merge. */
export type ResolvedSiteRoiAssumptions = {
  traditionalInspectionTeamPerAsset: number
  niiPeoplePerAsset: number
  niiRateAedPerM2: number
  /** Per eligible person, for the whole TAR (not per day). */
  hseIncidentProbPerPersonPerTar: number
  hseDownsideAed: number
  criticalPathReductionPercent: number
  tarDurationDays: number
  dailyRatePerPersonAed: number
  dailyProductionAed: number
  inspectionOnCriticalPath: boolean
}

export const DEFAULT_RESOLVED_SITE_ROI_ASSUMPTIONS: ResolvedSiteRoiAssumptions =
  {
    traditionalInspectionTeamPerAsset: 8,
    niiPeoplePerAsset: 2,
    niiRateAedPerM2: 85,
    hseIncidentProbPerPersonPerTar: 0.00025,
    hseDownsideAed: 500_000,
    criticalPathReductionPercent: 5,
    tarDurationDays: 0,
    dailyRatePerPersonAed: 0,
    dailyProductionAed: 0,
    inspectionOnCriticalPath: false,
  }

function storedHseProb(
  stored: SiteRoiAssumptionsValue | undefined
): number | undefined {
  return (
    stored?.hseIncidentProbPerPersonPerTar ??
    stored?.hseIncidentProbPerPersonPerDay
  )
}

/**
 * Global-scope fields: prefer `globalStored`, then legacy copies on `siteStored`, then defaults.
 */
function resolveGlobalSlice(
  globalStored: SiteRoiAssumptionsValue | undefined,
  siteStored: SiteRoiAssumptionsValue | undefined
): Pick<
  ResolvedSiteRoiAssumptions,
  | 'traditionalInspectionTeamPerAsset'
  | 'niiPeoplePerAsset'
  | 'niiRateAedPerM2'
  | 'hseIncidentProbPerPersonPerTar'
  | 'hseDownsideAed'
  | 'criticalPathReductionPercent'
> {
  const d = DEFAULT_RESOLVED_SITE_ROI_ASSUMPTIONS
  const pick = <K extends keyof SiteRoiAssumptionsValue>(
    key: K,
    fallback: number
  ): number => {
    const gv = globalStored?.[key]
    const sv = siteStored?.[key]
    if (typeof gv === 'number') return gv
    if (typeof sv === 'number') return sv
    return fallback
  }
  const hseP =
    storedHseProb(globalStored) ??
    storedHseProb(siteStored) ??
    d.hseIncidentProbPerPersonPerTar

  return {
    traditionalInspectionTeamPerAsset: pick(
      'traditionalInspectionTeamPerAsset',
      d.traditionalInspectionTeamPerAsset
    ),
    niiPeoplePerAsset: pick('niiPeoplePerAsset', d.niiPeoplePerAsset),
    niiRateAedPerM2: pick('niiRateAedPerM2', d.niiRateAedPerM2),
    hseIncidentProbPerPersonPerTar: hseP,
    hseDownsideAed: pick('hseDownsideAed', d.hseDownsideAed),
    criticalPathReductionPercent: pick(
      'criticalPathReductionPercent',
      d.criticalPathReductionPercent
    ),
  }
}

/**
 * TAR-level fields: only from the site row (+ defaults).
 */
function resolveTarSlice(
  siteStored: SiteRoiAssumptionsValue | undefined
): Pick<
  ResolvedSiteRoiAssumptions,
  | 'tarDurationDays'
  | 'dailyRatePerPersonAed'
  | 'dailyProductionAed'
  | 'inspectionOnCriticalPath'
> {
  const d = DEFAULT_RESOLVED_SITE_ROI_ASSUMPTIONS
  return {
    tarDurationDays: siteStored?.tarDurationDays ?? d.tarDurationDays,
    dailyRatePerPersonAed:
      siteStored?.dailyRatePerPersonAed ?? d.dailyRatePerPersonAed,
    dailyProductionAed: siteStored?.dailyProductionAed ?? d.dailyProductionAed,
    inspectionOnCriticalPath:
      siteStored?.inspectionOnCriticalPath ?? d.inspectionOnCriticalPath,
  }
}

/** Merge global row + per-site row (and legacy all-in-one site rows). */
export function mergeRoiAssumptionsForSite(
  globalStored: SiteRoiAssumptionsValue | undefined,
  siteStored: SiteRoiAssumptionsValue | undefined
): ResolvedSiteRoiAssumptions {
  return {
    ...resolveGlobalSlice(globalStored, siteStored),
    ...resolveTarSlice(siteStored),
  }
}

function isNiiEligibleForRoi(ticket: LibraTicket): boolean {
  if (!ticket.workflowComplete) return false
  const v = ticket.fieldValues?.eligibility?.value as
    | EligibilityStatus
    | undefined
  return v === ELIGIBILITY_STATUS.ELIGIBLE
}

export function getTicketAreaM2(ticket: LibraTicket): number {
  return (
    unwrapFieldValue<number>(ticket.fieldValues[INSPECTABLE_AREA_M2_FIELD]) ?? 0
  )
}

/**
 * `tickets` should be version-filtered already (e.g. getLatestTicketPerAssetForStats).
 */
export function pickRoiEligibleTickets(
  tickets: ReadonlyArray<LibraTicket>
): LibraTicket[] {
  return tickets.filter(isNiiEligibleForRoi)
}

function siteKeyForTicket(ticket: LibraTicket): string {
  const s = ticket.tags.site
  if (typeof s === 'string' && s) return s
  return UNKNOWN_SITE_KEY
}

/**
 * Total NII saving (delta model):
 * - Labor: PeopleSaved × daily rate × TAR days
 * - Inspection: (traditional team/asset × n_assets × daily rate × TAR days) − Σ (NII rate × area)
 * - HSE: PeopleSaved × PPerTar × downside
 * - Production: (CP% / 100) × TAR days × daily production when inspection is on critical path
 *
 * PeopleSaved = NII-eligible asset count × (traditional team per asset − NII people per asset).
 */
export type NiiSavingsSiteWorkbook = {
  niiAssetCount: number
  peopleSaved: number
  traditionalTeamPerAsset: number
  niiPeoplePerAsset: number
  tarDurationDays: number
  hseP: number
  hseDownsideAed: number
  /** traditional team/asset × n_assets × daily rate × TAR days */
  inspBau: number
  /** Σ round(NII AED/m² × area) per asset */
  inspNii: number
  niiRateAedPerM2: number
  ass: ResolvedSiteRoiAssumptions
}

function computeNiiSavingsForSiteWithWorkbook(
  niiAssets: ReadonlyArray<{ areaM2: number }>,
  a: ResolvedSiteRoiAssumptions
): {
  breakdown: RoiSavingsBreakdown
  workbook: NiiSavingsSiteWorkbook
} {
  if (niiAssets.length === 0) {
    return {
      breakdown: {
        inspection: 0,
        hse: 0,
        production: 0,
        labor: 0,
        total: 0,
      },
      workbook: {
        niiAssetCount: 0,
        peopleSaved: 0,
        traditionalTeamPerAsset: a.traditionalInspectionTeamPerAsset,
        niiPeoplePerAsset: a.niiPeoplePerAsset,
        tarDurationDays: a.tarDurationDays,
        hseP: a.hseIncidentProbPerPersonPerTar,
        hseDownsideAed: a.hseDownsideAed,
        inspBau: 0,
        inspNii: 0,
        niiRateAedPerM2: a.niiRateAedPerM2,
        ass: a,
      },
    }
  }

  const peoplePerDelta = Math.max(
    0,
    a.traditionalInspectionTeamPerAsset - a.niiPeoplePerAsset
  )
  const peopleSaved = niiAssets.length * peoplePerDelta

  const dur = a.tarDurationDays
  const criticalPathReductionFraction = a.criticalPathReductionPercent / 100

  const labor = peopleSaved * a.dailyRatePerPersonAed * dur

  const inspBau =
    a.traditionalInspectionTeamPerAsset *
    niiAssets.length *
    a.dailyRatePerPersonAed *
    dur
  const inspNii = niiAssets.reduce(
    (s, x) => s + Math.round(a.niiRateAedPerM2 * x.areaM2),
    0
  )
  const inspection = inspBau - inspNii

  const hse = peopleSaved * a.hseIncidentProbPerPersonPerTar * a.hseDownsideAed

  const production = a.inspectionOnCriticalPath
    ? criticalPathReductionFraction * dur * a.dailyProductionAed
    : 0

  const subtotal = inspection + hse + production + labor
  return {
    breakdown: {
      inspection,
      hse,
      production,
      labor,
      total: subtotal,
    },
    workbook: {
      niiAssetCount: niiAssets.length,
      peopleSaved,
      traditionalTeamPerAsset: a.traditionalInspectionTeamPerAsset,
      niiPeoplePerAsset: a.niiPeoplePerAsset,
      tarDurationDays: dur,
      hseP: a.hseIncidentProbPerPersonPerTar,
      hseDownsideAed: a.hseDownsideAed,
      inspBau,
      inspNii,
      niiRateAedPerM2: a.niiRateAedPerM2,
      ass: a,
    },
  }
}

export function computeNiiSavingsForSite(
  niiAssets: ReadonlyArray<{ areaM2: number }>,
  a: ResolvedSiteRoiAssumptions
): RoiSavingsBreakdown {
  return computeNiiSavingsForSiteWithWorkbook(niiAssets, a).breakdown
}

export type RoiBarTooltip = { title: string; lines: string[] }

type SiteRoiMap =
  | Readonly<Record<string, SiteRoiAssumptionsValue | undefined>>
  | undefined

/** `data['tar-roi']` on the libra-ticket-group (per-site TAR/ROI form). */
export function getTarRoiBySiteFromGroup(group: LibraTicketGroup): SiteRoiMap {
  return group[TAR_ROI_GROUP_DATA_KEY]
}

export const ROI_CATEGORY_COLORS: Record<RoiSavingsCategory, string> = {
  inspection: '#4caf6e',
  hse: '#f97316',
  production: '#4a90d9',
  labor: '#a78bfa',
}

export const ROI_CATEGORY_LABELS: Record<RoiSavingsCategory, string> = {
  inspection: 'Inspection saving',
  hse: 'HSE saving',
  production: 'Production saving',
  labor: 'Labor saving',
}

export function formatAed(value: number): string {
  if (value === 0 || Number.isNaN(value)) {
    return 'AED 0'
  }
  if (value >= 1_000_000_000) {
    return `AED ${(value / 1_000_000_000).toFixed(1)}B`
  }
  if (value >= 1_000_000) {
    return `AED ${(value / 1_000_000).toFixed(1)}M`
  }
  if (value >= 1_000) {
    return `AED ${(value / 1_000).toFixed(0)}K`
  }
  return `AED ${value.toLocaleString()}`
}

/** Full precision for tooltips (e.g. hover on ROI tracker). */
export function formatAedFull(value: number): string {
  if (value === 0 || Number.isNaN(value)) {
    return 'AED 0'
  }
  const rounded = Math.round(value * 100) / 100
  return `AED ${rounded.toLocaleString('en-GB', { maximumFractionDigits: 2 })}`
}

function buildBarTooltips(
  byCategory: RoiSavingsBreakdown,
  perSite: ReadonlyArray<{
    siteId: string
    breakdown: RoiSavingsBreakdown
    workbook: NiiSavingsSiteWorkbook
  }>,
  inspRollBau: number,
  inspRollNii: number
): Record<RoiSavingsCategory, RoiBarTooltip> {
  const w = perSite.length === 1 ? perSite[0]!.workbook : null
  return {
    inspection: {
      title: ROI_CATEGORY_LABELS.inspection,
      lines: [
        'Traditional inspection cost = traditional team/asset × n_eligible_assets × daily rate × TAR days. NII cost = Σ (NII AED/m² × area). Saving is the difference.',
        ...(w
          ? [
              `Traditional: ${w.traditionalTeamPerAsset} people/asset × ${w.niiAssetCount} assets × ${w.ass.dailyRatePerPersonAed} AED/day × ${w.tarDurationDays} days = ${formatAedFull(w.inspBau)}`,
              `NII rate: ${w.niiRateAedPerM2} AED/m² × Σ area = ${formatAedFull(w.inspNii)}`,
            ]
          : []),
        `Sum traditional = ${formatAedFull(inspRollBau)}  −  Sum NII = ${formatAedFull(
          inspRollNii
        )}`,
        `Saving = ${formatAedFull(byCategory.inspection)}`,
      ],
    },
    hse: {
      title: ROI_CATEGORY_LABELS.hse,
      lines: w
        ? [
            `People saved = eligible assets × (traditional team/asset − NII people/asset) = ${w.niiAssetCount} × (${w.traditionalTeamPerAsset} − ${w.niiPeoplePerAsset}) = ${w.peopleSaved}.`,
            `HSE = people saved × incident probability (per person, per TAR) × downside.`,
            `= ${w.peopleSaved} × ${w.hseP} × ${w.hseDownsideAed.toLocaleString('en-GB')} = ${formatAedFull(byCategory.hse)}`,
          ]
        : [
            'Per site: people saved × P (per person, per TAR) × downside; bar is the sum.',
            `Total = ${formatAedFull(byCategory.hse)}`,
          ],
    },
    production: {
      title: ROI_CATEGORY_LABELS.production,
      lines: w
        ? w.ass.inspectionOnCriticalPath
          ? [
              'Production = (critical path reduction % ÷ 100) × TAR days × daily production value when inspection is on the critical path.',
              `= (${w.ass.criticalPathReductionPercent}/100) × ${
                w.tarDurationDays
              } × ${w.ass.dailyProductionAed} = ${formatAedFull(
                byCategory.production
              )}`,
            ]
          : [
              'Inspection is not on the critical path in settings, so production saving is 0.',
              `= ${formatAedFull(byCategory.production)}`,
            ]
        : [
            'Per site: apply global critical-path % when on critical path; then sum.',
            `Total = ${formatAedFull(byCategory.production)}`,
          ],
    },
    labor: {
      title: ROI_CATEGORY_LABELS.labor,
      lines: w
        ? [
            'Labor = people saved × daily rate per person × TAR days.',
            `${w.peopleSaved} × ${w.ass.dailyRatePerPersonAed} × ${w.tarDurationDays} d = ${formatAedFull(
              byCategory.labor
            )}`,
          ]
        : [
            'Per site: (people saved) × (daily rate) × (TAR days); then sum.',
            `Total = ${formatAedFull(byCategory.labor)}`,
          ],
    },
  }
}

const EMPTY_BAR_TOOLTIPS: Record<RoiSavingsCategory, RoiBarTooltip> = {
  inspection: { title: ROI_CATEGORY_LABELS.inspection, lines: [] },
  hse: { title: ROI_CATEGORY_LABELS.hse, lines: [] },
  production: { title: ROI_CATEGORY_LABELS.production, lines: [] },
  labor: { title: ROI_CATEGORY_LABELS.labor, lines: [] },
}

export function aggregateNiiSavings(
  tickets: ReadonlyArray<LibraTicket>,
  tarRoiBySite: SiteRoiMap
): {
  byCategory: RoiSavingsBreakdown
  barTooltips: Record<RoiSavingsCategory, RoiBarTooltip>
  bySite: ReadonlyArray<{
    siteId: string
    breakdown: RoiSavingsBreakdown
    workbook: NiiSavingsSiteWorkbook
  }>
} {
  const roiTickets = pickRoiEligibleTickets(tickets)
  if (roiTickets.length === 0) {
    return {
      byCategory: { inspection: 0, hse: 0, production: 0, labor: 0, total: 0 },
      barTooltips: EMPTY_BAR_TOOLTIPS,
      bySite: [],
    }
  }

  const globalStored = tarRoiBySite?.[TAR_ROI_GLOBAL_STORAGE_KEY]

  const bySiteGrouped = groupBy(roiTickets, siteKeyForTicket)
  const perSite: Array<{
    siteId: string
    breakdown: RoiSavingsBreakdown
    workbook: NiiSavingsSiteWorkbook
  }> = []

  for (const [key, list] of Object.entries(bySiteGrouped)) {
    if (!list?.length) continue
    const siteId = key
    const siteRow =
      siteId === UNKNOWN_SITE_KEY ? undefined : tarRoiBySite?.[siteId]
    const a = mergeRoiAssumptionsForSite(globalStored, siteRow)
    const assets = list.map((t) => ({
      areaM2: getTicketAreaM2(t),
    }))
    const { breakdown, workbook } = computeNiiSavingsForSiteWithWorkbook(
      assets,
      a
    )
    perSite.push({ siteId, breakdown, workbook })
  }

  let inspRollBau = 0
  let inspRollNii = 0
  const byCategory: RoiSavingsBreakdown = {
    inspection: 0,
    hse: 0,
    production: 0,
    labor: 0,
    total: 0,
  }
  for (const s of perSite) {
    inspRollBau += s.workbook.inspBau
    inspRollNii += s.workbook.inspNii
    byCategory.inspection += s.breakdown.inspection
    byCategory.hse += s.breakdown.hse
    byCategory.production += s.breakdown.production
    byCategory.labor += s.breakdown.labor
  }
  byCategory.total =
    byCategory.inspection +
    byCategory.hse +
    byCategory.production +
    byCategory.labor

  return {
    byCategory,
    barTooltips: buildBarTooltips(
      byCategory,
      perSite,
      inspRollBau,
      inspRollNii
    ),
    bySite: perSite,
  }
}

export function getDistinctSiteIdsFromTickets(
  tickets: ReadonlyArray<LibraTicket>
): string[] {
  return uniq(
    tickets
      .map((t) => t.tags.site)
      .filter((s): s is string => typeof s === 'string' && s.length > 0)
  )
}
